import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { useForecastReport } from "../hooks/useForecastReport";
import { YEAR_COLOR_PALETTE } from "../models/ForecastReport";
import { ForecastReportToolbar } from "./ForecastReportToolbar";
import { ForecastReportYearsTable } from "./ForecastReportYearsTable";
import { ForecastReportAnalyticsTable } from "./ForecastReportAnalyticsTable";
import { ForecastReportChart } from "./ForecastReportChart";

const OUTER_LAYOUT = { left: 35, right: 65 };
const INNER_LAYOUT = { years: 40, analytics: 60 };
// Never narrower than the analytics columns, so that grid has no horizontal scroll
const LEFT_MIN_SIZE = "530px";

const parseYear = (raw: string | undefined, fallback: number): number => {
	const n = raw ? Number(raw) : Number.NaN;
	return Number.isFinite(n) && n > 1900 && n < 2200 ? n : fallback;
};

const URL_SCHEMA = {
	// The two years stay in the path, where the menu links point. The mode is a filter.
	mode: urlParam.oneOf(["Actual", "Forecast", "Mixed"] as const, "Actual"),
};

export const ForecastReportManager = () => {
	const { startYear: startParam, endYear: endParam } = useParams<{
		startYear?: string;
		endYear?: string;
	}>();
	const navigate = useNavigate();

	const basePath = "/reports/forecast-report";

	const currentYear = new Date().getFullYear();
	const defaults = useMemo(
		() => ({ start: currentYear - 3, end: currentYear }),
		[currentYear],
	);

	const initial = useMemo(() => {
		let start = parseYear(startParam, defaults.start);
		let end = parseYear(endParam, defaults.end);
		if (start > end) {
			end = start;
		}
		return { start, end };
	}, [startParam, endParam, defaults]);

	const [startYear, setStartYear] = useState<number>(initial.start);
	const [endYear, setEndYear] = useState<number>(initial.end);
	const [{ mode }, setFilters] = useUrlState(URL_SCHEMA);
	const [visibleYears, setVisibleYears] = useState<Set<number>>(new Set());
	const [errorMsg, setErrorMsg] = useState("");

	useEffect(() => {
		setStartYear(initial.start);
		setEndYear(initial.end);
	}, [initial]);

	const outerLayout = useDefaultLayout({
		id: "forecast-report-outer-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const innerLayout = useDefaultLayout({
		id: "forecast-report-inner-v1",
		panelIds: ["years", "analytics"],
		storage: localStorage,
	});

	// Plant time: mesf-core sets moment's default zone from the TimeZone setting
	const currentMonth = moment().startOf("month").format("YYYY-MM-DD");

	const dataQuery = useForecastReport({
		startYear,
		endYear,
		currentMonth,
	});

	const years = useMemo(
		() => dataQuery.data?.years ?? [],
		[dataQuery.data?.years],
	);
	const monthly = useMemo(
		() => dataQuery.data?.monthly ?? [],
		[dataQuery.data?.monthly],
	);
	const analytics = useMemo(
		() => dataQuery.data?.analytics ?? [],
		[dataQuery.data?.analytics],
	);

	useEffect(() => {
		if (years.length === 0) return;
		setVisibleYears(new Set(years.map((y) => y.Year)));
	}, [years]);

	// Counted back from the end year: the newest year always gets the first color,
	// and moving the start year does not repaint the others
	const colorForYear = useCallback(
		(year: number): string => {
			const idx = Math.max(endYear - year, 0);
			return YEAR_COLOR_PALETTE[idx % YEAR_COLOR_PALETTE.length];
		},
		[endYear],
	);

	const handleYearToggle = useCallback((year: number) => {
		setVisibleYears((prev) => {
			const next = new Set(prev);
			if (next.has(year)) next.delete(year);
			else next.add(year);
			return next;
		});
	}, []);

	const pushUrl = useCallback(
		(s: number, e: number) => {
			// Replace, so Back leaves the page instead of stepping through every year range
			// looked at, and keep the query string so the mode survives a year change.
			navigate(
				{ pathname: `${basePath}/${s}/${e}`, search: window.location.search },
				{ replace: true },
			);
		},
		[basePath, navigate],
	);

	const handleStartYearChange = useCallback(
		(y: number) => {
			const next = Math.max(1900, Math.min(2199, y));
			const nextEnd = next > endYear ? next : endYear;
			setStartYear(next);
			if (nextEnd !== endYear) setEndYear(nextEnd);
			pushUrl(next, nextEnd);
		},
		[endYear, pushUrl],
	);

	const handleEndYearChange = useCallback(
		(y: number) => {
			const next = Math.max(1900, Math.min(2199, y));
			const nextStart = next < startYear ? next : startYear;
			setEndYear(next);
			if (nextStart !== startYear) setStartYear(nextStart);
			pushUrl(nextStart, next);
		},
		[startYear, pushUrl],
	);

	useEffect(() => {
		if (dataQuery.error && !errorMsg) {
			setErrorMsg((dataQuery.error as Error).message);
		}
	}, [dataQuery.error, errorMsg]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Forecast Report
			</Typography>
			<Paper elevation={3}>
				<ForecastReportToolbar
					onReset={() =>
						navigate({ pathname: basePath, search: "" }, { replace: true })
					}
					canReset={
						startYear !== defaults.start ||
						endYear !== defaults.end ||
						mode !== "Actual"
					}
					startYear={startYear}
					endYear={endYear}
					mode={mode}
					onStartYearChange={handleStartYearChange}
					onEndYearChange={handleEndYearChange}
					onModeChange={(value) => setFilters({ mode: value })}
					isLoading={dataQuery.isFetching}
				/>
			</Paper>

			<Group
				id="forecast-report-outer"
				orientation="horizontal"
				defaultLayout={outerLayout.defaultLayout ?? OUTER_LAYOUT}
				onLayoutChange={outerLayout.onLayoutChange}
				style={{ flex: 1, width: "100%", minHeight: 0 }}
			>
				<Panel id="left" minSize={LEFT_MIN_SIZE}>
					<Box sx={{ pr: 1, height: "100%" }}>
						<Group
							id="forecast-report-inner"
							orientation="vertical"
							defaultLayout={innerLayout.defaultLayout ?? INNER_LAYOUT}
							onLayoutChange={innerLayout.onLayoutChange}
							style={{ height: "100%" }}
						>
							<Panel id="years" minSize={15} style={{ paddingBottom: 5 }}>
								<Paper
									elevation={3}
									sx={{ height: "100%", width: "100%", overflow: "hidden" }}
								>
									{dataQuery.isLoading ? (
										<CenteredLazyLoading />
									) : (
										<ForecastReportYearsTable
											rows={years}
											visibleYears={visibleYears}
											onYearToggle={handleYearToggle}
											colorForYear={colorForYear}
										/>
									)}
								</Paper>
							</Panel>
							<Separator
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									cursor: "row-resize",
									padding: "2px 0",
								}}
							>
								<Box
									sx={{
										height: 6,
										width: 48,
										borderRadius: 1,
										backgroundColor: "#e0e0e0",
										display: "flex",
										flexDirection: "row",
										alignItems: "center",
										justifyContent: "center",
										gap: "3px",
										transition: "background-color 0.2s",
										"&:hover": {
											backgroundColor: "primary.main",
											"& .grip-dot": { backgroundColor: "#fff" },
										},
									}}
								>
									{[...Array(5)].map((_, i) => (
										<Box
											key={`vgrip-dot-${i}`}
											className="grip-dot"
											sx={{
												width: 4,
												height: 4,
												borderRadius: "50%",
												backgroundColor: "#9e9e9e",
												transition: "background-color 0.2s",
											}}
										/>
									))}
								</Box>
							</Separator>
							<Panel id="analytics" minSize={20} style={{ paddingBottom: 5 }}>
								<Paper
									elevation={3}
									sx={{ height: "100%", width: "100%", overflow: "hidden" }}
								>
									{dataQuery.isLoading ? (
										<CenteredLazyLoading />
									) : (
										<ForecastReportAnalyticsTable
											rows={analytics}
											currentMonth={currentMonth}
										/>
									)}
								</Paper>
							</Panel>
						</Group>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 48,
							borderRadius: 1,
							backgroundColor: "#e0e0e0",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: "3px",
							transition: "background-color 0.2s",
							"&:hover": {
								backgroundColor: "primary.main",
								"& .grip-dot": { backgroundColor: "#fff" },
							},
						}}
					>
						{[...Array(5)].map((_, i) => (
							<Box
								key={`hgrip-dot-${i}`}
								className="grip-dot"
								sx={{
									width: 4,
									height: 4,
									borderRadius: "50%",
									backgroundColor: "#9e9e9e",
									transition: "background-color 0.2s",
								}}
							/>
						))}
					</Box>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper elevation={3} sx={{ height: "100%", p: 1 }}>
							{dataQuery.isLoading ? (
								<CenteredLazyLoading />
							) : (
								<ForecastReportChart
									mode={mode}
									visibleYears={visibleYears}
									rows={monthly}
									colorForYear={colorForYear}
								/>
							)}
						</Paper>
					</Box>
				</Panel>
			</Group>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
